/**
 * Track an element's rendered height (px) via `ResizeObserver`, kept in state
 * so a change re-renders the consumer. Used to feed the sticky app-bar +
 * banner ("top") and composer ("bottom") insets into `ChatMessagesRegion` →
 * `VirtualList`'s `reversedBottomPinInset` (review fix plan 4, Findings 1-2).
 *
 * Returns a `[height, callbackRef]` pair. The callback ref (not a static
 * `useRef`) is what makes this correct across the page's loading gate: the
 * observed wrappers do not exist in the gate's render tree, so a static-ref
 * `useLayoutEffect` would fire once against a `null` node and never re-attach.
 * The callback ref instead (dis)connects the observer exactly when React
 * mounts / unmounts the node. The observer keeps the height current as the
 * composer grows (multiline textarea), the banner toggles, or the safe-area
 * inset changes on orientation flip. Returns 0 until first measured.
 */
import { useCallback, useRef, useState } from 'react';

export const useMeasuredHeight = (): [number, (node: HTMLElement | null) => void] => {
  const [height, setHeight] = useState<number>(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const callbackRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    if (!node) {
      observerRef.current = null;
      return;
    }
    setHeight(node.getBoundingClientRect().height);
    const ro = new ResizeObserver(() =>
      setHeight(node.getBoundingClientRect().height),
    );
    ro.observe(node);
    observerRef.current = ro;
  }, []);
  return [height, callbackRef];
};
